import axios from "axios";

// Vuoto = stesso indirizzo del sito (così la stessa build funziona su localhost e sul dominio).
const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const http = axios.create({ baseURL: BASE, withCredentials: true });

const TOKEN_KEY = "dc_session_token";
let memToken = null;
function readToken() { if (memToken) return memToken; try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } }
export const setToken = (t) => { if (!t) return; memToken = t; try { localStorage.setItem(TOKEN_KEY, t); } catch {} };
export const clearToken = () => { memToken = null; try { localStorage.removeItem(TOKEN_KEY); } catch {} };
http.interceptors.request.use((c) => {
  const t = readToken(); if (t) c.headers.Authorization = `Bearer ${t}`;
  if ((c.method || "get").toLowerCase() !== "get") lastWrite = Date.now();
  return c;
});

// Ora dell'ultima modifica fatta da questo browser: serve a non mostrare come «arrivata da Discord»
// la conferma di una modifica appena fatta dal sito.
let lastWrite = 0;
export const msSinceLocalWrite = () => Date.now() - lastWrite;

// Se il bot è offline il backend mette la modifica in coda (HTTP 202): avvisiamo una volta sola, ovunque.
const queuedListeners = new Set();
export const onQueued = (fn) => { queuedListeners.add(fn); return () => queuedListeners.delete(fn); };
http.interceptors.response.use((r) => {
  if (r.status === 202 && r.data?.queued) queuedListeners.forEach((fn) => fn(r.data));
  return r;
});

// Nomi comprensibili per i campi segnalati dal bot (es. "embeds.0.image" → "Embed 1 › Immagine grande").
const FIELD_NAMES = {
  embeds: "Embed", buttons: "Bottone", fields: "Campo", content: "Testo del messaggio", title: "Titolo", description: "Descrizione",
  url: "Link", image: "Immagine grande", thumbnail: "Miniatura", author: "Autore", footer: "Footer", iconUrl: "Icona", name: "Nome",
  value: "Valore", text: "Testo", label: "Testo del bottone", emoji: "Emoji", roleId: "Ruolo", customId: "ID personalizzato", color: "Colore",
};
const fieldLabel = (path) => String(path || "").split(".").map((p) => (/^\d+$/.test(p) ? String(Number(p) + 1) : FIELD_NAMES[p] || p))
  .join(" ").replace(/ (\d+)/g, " $1 ›").replace(/›\s*$/, "").trim();

export function apiError(e) {
  const d = e?.response?.data;
  if (d?.detail && typeof d.detail === "string") return d.detail;
  if (d?.error && Array.isArray(d.details) && d.details.length) {
    return `${d.error}: ${d.details.slice(0, 3).map((x) => `${fieldLabel(x.path)} — ${x.message}`).join(" · ")}`;
  }
  if (d?.error) return d.error;
  if (Array.isArray(d?.detail)) return d.detail.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Errore imprevisto";
}

export const auth = {
  login: (password) => http.post("/auth/login", { password }).then((r) => r.data),
  loginUser: (email, password) => http.post("/auth/login-user", { email, password }).then((r) => r.data),
  register: (email, password, displayName) => http.post("/auth/register", { email, password, displayName }).then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  changePassword: (currentPassword, newPassword) => http.post("/auth/change-password", { currentPassword, newPassword }).then((r) => r.data),
  users: () => http.get("/auth/users").then((r) => r.data),
  approve: (approvalCode, permissions) => http.post("/auth/approve", { approvalCode, permissions }).then((r) => r.data),
  updateUser: (id, patch) => http.put(`/auth/users/${id}`, patch).then((r) => r.data),
  deleteUser: (id) => http.delete(`/auth/users/${id}`).then((r) => r.data),
};

export const api = {
  sync: () => http.post("/sync").then((r) => r.data),
  overview: () => http.get("/overview").then((r) => r.data),
  roles: () => http.get("/roles").then((r) => r.data),
  channels: () => http.get("/channels").then((r) => r.data),
  members: (params) => http.get("/members", { params }).then((r) => r.data),
  applyRole: (roleId, target, value) => http.post(`/roles/${roleId}/apply`, { target, value }).then((r) => r.data),
  removeRole: (roleId, target, value) => http.post(`/roles/${roleId}/remove`, { target, value }).then((r) => r.data),
  jobs: () => http.get("/jobs").then((r) => r.data),
  job: (id) => http.get(`/jobs/${id}`).then((r) => r.data),
  memberAddRole: (memberId, roleId) => http.put(`/members/${memberId}/roles/${roleId}`).then((r) => r.data),
  memberRemoveRole: (memberId, roleId) => http.delete(`/members/${memberId}/roles/${roleId}`).then((r) => r.data),
  audit: () => http.get("/audit").then((r) => r.data),
  events: () => http.get("/events").then((r) => r.data),
  statsHistory: () => http.get("/stats/history").then((r) => r.data),
};

export const backups = {
  list: () => http.get("/backups").then((r) => r.data),
  create: (name) => http.post("/backups", { name }).then((r) => r.data),
  get: (id) => http.get(`/backups/${id}`).then((r) => r.data),
  remove: (id) => http.delete(`/backups/${id}`).then((r) => r.data),
};

export const maintenance = {
  list: () => http.get("/maintenance/snapshots").then((r) => r.data),
  create: (name) => http.post("/maintenance/snapshots", { name }).then((r) => r.data),
  get: (id) => http.get(`/maintenance/snapshots/${id}`).then((r) => r.data),
  apply: (id, deleteMissing) => http.post(`/maintenance/snapshots/${id}/apply`, { deleteMissing }).then((r) => r.data),
  remove: (id) => http.delete(`/maintenance/snapshots/${id}`).then((r) => r.data),
};

// proxy bot per feature esistenti (embed, automazioni, struttura)
const bot = (m) => (path, opts = {}) => http.request({ method: m, url: `/bot/${path}`, ...opts }).then((r) => r.data);
export const botGet = (p, params) => bot("get")(p, { params });
export const botPost = (p, data) => bot("post")(p, { data });
export const botPatch = (p, data) => bot("patch")(p, { data });
export const botPut = (p, data) => bot("put")(p, { data });
export const botDelete = (p) => bot("delete")(p);
export const templates = {
  list: () => http.get("/templates").then((r) => r.data),
  save: (t) => http.post("/templates", t).then((r) => r.data),
  remove: (id) => http.delete(`/templates/${id}`).then((r) => r.data),
};
export const botStatusApi = {
  get: () => http.get("/bot-status").then((r) => r.data),
  reconnect: () => http.post("/bot-status/reconnect", null, { timeout: 90000 }).then((r) => r.data),
  removePending: (id) => http.delete(`/bot-status/pending/${id}`).then((r) => r.data),
  clearPending: () => http.delete("/bot-status/pending").then((r) => r.data),
};
export const initialSyncApi = {
  info: () => http.get("/sync/initial").then((r) => r.data),
  run: () => http.post("/sync/initial", null, { timeout: 120000 }).then((r) => r.data),
};
export const demoTools = {
  offline: (offline) => http.post("/demo/offline", { offline }).then((r) => r.data),
  discordChange: () => http.post("/demo/discord-change").then((r) => r.data),
};

// Flusso in tempo reale (Server-Sent Events) letto con fetch per poter inviare l'header di autenticazione.
export function openLiveStream(onEvent, onState) {
  let stopped = false;
  let ctrl = null;
  const run = async () => {
    while (!stopped) {
      ctrl = new AbortController();
      try {
        const t = readToken();
        const res = await fetch(`${BASE}/live`, { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {}, signal: ctrl.signal });
        if (!res.ok || !res.body) throw new Error(`live ${res.status}`);
        onState(true);
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let i;
          while ((i = buf.indexOf("\n\n")) !== -1) {
            const chunk = buf.slice(0, i); buf = buf.slice(i + 2);
            const data = chunk.split("\n").filter((l) => l.startsWith("data: ")).map((l) => l.slice(6)).join("\n");
            if (data) { try { onEvent(JSON.parse(data)); } catch { /* evento non valido */ } }
          }
        }
      } catch { /* rete assente o server riavviato: si riprova */ }
      if (stopped) break;
      onState(false);
      await new Promise((r) => setTimeout(r, 3000));
    }
  };
  run();
  return () => { stopped = true; ctrl?.abort(); };
}
export const overviewApi = api.overview;
export const eventsApi = api.events;
export const auditApi = api.audit;
export { http };
